import { Location } from 'history';
import * as React from 'react';
import { generatePath, matchPath, useLocation } from 'react-router';
import routes from 'config/developerSite/routes';
import { TrackedRedirect } from './TrackedRedirect';

export interface LegacyRedirectRule {
  /** Logged to analytics when this redirect happens. */
  id: string;
  /**
   * URL to redirect from. A string is matched like a route path, so it matches the start of the URL unless
   * `exact` is set. A regular expression matches anywhere in the pathname. Omit it to match every URL.
   */
  from?: string | RegExp;
  /** The part of the URL (path and hash) that `to` replaces. Defaults to `from`. */
  replace?: string | RegExp;
  /** Where to redirect. String route parameters are filled from `from`. Regex capture groups can be used as `$1`, `$2`, and so on. */
  to: string;
  /** Only match when `from` matches the whole pathname. */
  exact?: boolean;
  /** Keep the query string on the redirected URL. */
  preserveSearch?: boolean;
}

/**
 * Redirects legacy URLs. Rules are tried in order and the first match wins. When no rule matches,
 * `children` render, so wrap the routes that follow the redirects.
 */
export const LegacyRedirects: React.FC<React.PropsWithChildren<{ rules: LegacyRedirectRule[] }>> = ({
  rules,
  children,
}) => {
  const location = useLocation();
  const rule = rules.find((candidate) => redirectsAway(candidate, location));
  const target = rule && getRedirectTarget(rule, location);

  if (!rule || !target) return <>{children}</>;

  return <TrackedRedirect id={rule.id} to={target} />;
};

/** Ordered legacy URL redirects for the whole site. Matomo logs show when each one was last used. */
export const CLOUD_REDIRECT_RULES: LegacyRedirectRule[] = [
  {
    id: 'refapp-home-with-hash',
    from: '/reference-apps/index.html',
    replace: /\/reference-apps\/index.html#(.*)/,
    to: routes.appCatalogView('$1'),
  },
  { id: 'refapp-home', from: '/reference-apps/index.html', to: routes.appCatalog },
  { id: 'refapp-details1', from: /\/reference-apps\/(.*)\.html/, to: routes.appCatalogView('$1') },
  { id: 'refapp-details2', from: /\/reference-apps\/details\/(.*)/, to: routes.appCatalogView('$1') },
  { id: 'refapp-details3', from: /\/reference-apps\/(.*)/, to: routes.appCatalogView('$1') },
  { id: 'refapp-home2', from: '/reference-apps', to: routes.appCatalog },
  {
    id: 'doc-section',
    from: /\/documentation\/(.*)\/section\/(.*)/,
    to: routes.documentation + '/$1/#section-$2',
  },
  { id: 'console1', from: '/developer-dashboard', to: routes.console },
  { id: 'console2', from: '/developer_dashboard.html', to: routes.console },
  { id: 'console3', from: '/app-console', to: routes.console },
  { id: 'console4', from: /\/app-console\/(.*)/, to: `${routes.console}/$1` },
  { id: 'all-apps', from: '/console/apps/all', to: routes.consoleApps, preserveSearch: true },
  { id: 'all-clients', from: '/console/clients/all', to: routes.clients },
  {
    id: 'enable-tenant1',
    from: '/getting_started/enable_tenant.html',
    to: `${routes.documentation}/tsm1574197307343`,
  },
  { id: 'enable-tenant2', from: '/enable-tenant', to: `${routes.documentation}/tsm1574197307343` },
  { id: 'home1', from: '/index.html', to: routes.console },
  { id: 'home2', from: '/home', to: routes.console },
  {
    id: 'terms-index',
    from: '/terms/index.html',
    replace: /\/terms\/index\.html(#(.*-)*(.*)(-(.*))+)?/,
    to: `${routes.terms}/$3$5`,
  },
  { id: 'terms-legacy', from: '/terms/workdayextend', to: routes.termsHome },
  { id: 'terms-legacy-wcpterms', from: '/terms/wcpterms', to: routes.termsHome },
  {
    id: 'terms-overview',
    from: '/terms/overview.html',
    replace: /\/terms\/overview\.html(#(.*-)*(.*)(-(.*))+)?/,
    to: `${routes.terms}/$3$5`,
  },
  { id: 'terms-overview-path', from: /\/terms\/overview\/(.*)/, to: `${routes.terms}/$1` },
  { id: 'terms-overview-root', from: '/terms/overview', to: routes.terms, exact: true },
  { id: 'general-index-with-hash', from: '/:any*/index.html', replace: /\/index\.html#?/, to: '' },
  { id: 'rest-api-explorer', from: '/explorer', to: routes.apiExplorer },
  { id: 'rest-api-explorer-links', from: /\/explorer\/(.*)/, to: `${routes.apiExplorer}/$1` },
  {
    id: 'rest-api-explorer-legacy-links',
    from: /\/rest-api-explorer\/(services|downloads)($|\/(.*))/,
    to: `${routes.apiExplorer}`,
  },
  { id: 'graph-api-explorer', from: '/graphapi', to: routes.graphQLExplorer },
  { id: 'graph-api-explorer-links', from: /\/graphapi\/(.*)/, to: `${routes.graphQLExplorer}/$1` },
  { id: 'terms', from: /\/terms\/(.*)\.html/, to: '/terms/$1' },
  { id: 'discover-intro', from: /\/discover(\/overview)(\/.*)/, to: routes.intro + '$1$2' },
  { id: 'discover-tutorials', from: '/discover/tutorials', to: routes.tutorials },
  { id: 'devsite-tutorials', from: /\/learn\/(.*)/, to: routes.tutorials },
  { id: 'flowbuilder-orchestrate', from: /\/flowbuilder(\/.*)/, to: routes.orchestrate + '$1' },
  { id: 'discover-root', from: '/discover', to: routes.intro },
  { id: 'workflows-learn-more', from: '/workflows-learn-more', to: routes.console },
  // Embedded link to the orchestration documentation in the PEX card creation XO task, see #pex-wcp-collab
  { id: 'doc-redirect-orch', from: '/doc-redirect/orch', to: `${routes.documentation}/hib1626197091901` },
  // App catalog renames
  { id: 'asset-tracking', from: '/app-catalog/assetTracking', to: '/app-catalog/assetBarcoding' },
  {
    id: 'home-card-performance-review',
    from: '/app-catalog/workdayTodayCardPerformanceReview',
    to: '/app-catalog/workdayHomeCardPerformanceReview',
  },
  { id: 'legacy-extend-the-power', from: '/extend-the-power-of-workday', to: routes.about },
];

/** Matches `from` the way a route in a `Switch` does, so a rule matches the same URLs a redirect route did. */
const matchesRule = ({ from, exact }: LegacyRedirectRule, pathname: string) => {
  if (!from) return true;
  if (typeof from === 'string') return !!matchPath(pathname, { path: from, exact });

  const match = copyPattern(from).exec(pathname);
  return !!match && (!exact || match[0] === pathname);
};

/** Skips rules that would leave the path unchanged, since redirecting to the current URL renders nothing. */
const redirectsAway = (rule: LegacyRedirectRule, location: Location) => {
  if (!matchesRule(rule, location.pathname)) return false;

  const path = rewritePath(rule, location);
  return path !== undefined && path !== location.pathname + location.hash;
};

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Global and sticky regexes keep lastIndex between calls, so copy those to keep shared rules stateless.
const copyPattern = (pattern: RegExp, ignoreCase = false) => {
  const flags = ignoreCase && !pattern.ignoreCase ? pattern.flags + 'i' : pattern.flags;
  return pattern.global || pattern.sticky || flags !== pattern.flags ? new RegExp(pattern.source, flags) : pattern;
};

/**
 * Returns the redirected path and hash, without the query string. Returns `undefined` when `to` has a route
 * parameter that `from` doesn't capture, so the rule is skipped instead of throwing during render.
 */
const rewritePath = ({ from, replace, to, exact }: LegacyRedirectRule, location: Location) => {
  const replaceValue = replace || from;
  if (!replaceValue) return to;

  // String `from` values match case-insensitively like routes, so their replacements must too.
  const ignoreCase = typeof from === 'string';
  const match = ignoreCase ? matchPath(location.pathname, { path: from, exact }) : null;
  // Use the concrete matched URL rather than the route pattern, which may contain parameters or wildcards.
  const matchedPath = !replace && match ? match.url : replaceValue;
  const pattern =
    typeof matchedPath === 'string'
      ? new RegExp(escapeRegExp(matchedPath), ignoreCase ? 'i' : '')
      : copyPattern(matchedPath, ignoreCase);

  const destination = !replace && match ? fillRouteParams(to, match.params) : to;
  if (destination === undefined) return undefined;

  return (location.pathname + location.hash).replace(pattern, destination);
};

/** Fills `to`'s route parameters, or returns `undefined` when one is missing, since `generatePath` throws. */
const fillRouteParams = (to: string, params: Record<string, string>) => {
  try {
    return generatePath(to, params);
  } catch {
    return undefined;
  }
};

const getRedirectTarget = (rule: LegacyRedirectRule, location: Location) => {
  const target = rewritePath(rule, location);
  if (!target || !rule.preserveSearch || !location.search) return target;

  // The query string belongs before the hash.
  const hashIndex = target.indexOf('#');
  return hashIndex === -1
    ? target + location.search
    : target.slice(0, hashIndex) + location.search + target.slice(hashIndex);
};
